// News.js
import React, { useState, useEffect } from 'react';
import { getAllNews } from '../api/news';
import { Card } from '../components/ui/Card';
import './News.css';

const News = () => {
  const [newsPosts, setNewsPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, updates, events, community, general
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllNews();
      setNewsPosts(data);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err.message || 'Failed to load news. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openPost = (post) => {
    setSelectedPost(post);
  };

  const closePost = () => {
    setSelectedPost(null);
  };

  const filterNews = () => {
    let filtered = newsPosts;

    // Filter by category
    if (filter !== 'all') {
      filtered = filtered.filter(post => 
        post.category?.toLowerCase() === filter.toLowerCase()
      );
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(post =>
        post.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      if (timestamp.toDate) {
        date = timestamp.toDate();
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };

  const filteredNews = filterNews();

  if (loading) {
    return (
      <div className="page news-page">
        <div className="loading">Loading news...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page news-page">
        <Card>
          <div className="error-message">
            <h2>Error</h2>
            <p>{error}</p>
            <button onClick={fetchNews}>Retry</button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="page news-page">
      <div className="news-header">
        <h1>News & Updates</h1>
        <p className="subtitle">Stay informed with the latest updates from our organization</p>
      </div>

      <div className="news-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search news articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filter === 'updates' ? 'active' : ''}`}
            onClick={() => setFilter('updates')}
          >
            Updates
          </button>
          <button
            className={`filter-btn ${filter === 'events' ? 'active' : ''}`}
            onClick={() => setFilter('events')}
          >
            Events
          </button>
          <button
            className={`filter-btn ${filter === 'community' ? 'active' : ''}`}
            onClick={() => setFilter('community')}
          >
            Community
          </button>
          <button
            className={`filter-btn ${filter === 'general' ? 'active' : ''}`}
            onClick={() => setFilter('general')}
          >
            General
          </button>
        </div>
      </div>

      {filteredNews.length === 0 ? (
        <div className="empty-state">
          <h3>No News Articles Found</h3>
          <p>
            {searchTerm || filter !== 'all'
              ? 'Try adjusting your filters or search term.'
              : 'There are no news articles at this time.'}
          </p>
        </div>
      ) : (
        <div className="news-grid">
          {filteredNews.map((post) => {
            const excerpt = post.content?.substring(0, 150) + '...' || '';

            return (
              <article
                key={post.id}
                className="news-card"
                onClick={() => openPost(post)}
              >
                {post.imageUrl && (
                  <div className="news-image">
                    <img src={post.imageUrl} alt={post.title} />
                  </div>
                )}
                <div className="news-card-content">
                  <div className="news-card-header">
                    {post.category && (
                      <span className={`category-badge ${post.category?.toLowerCase()}`}>
                        {post.category}
                      </span>
                    )}
                    <span className="news-date">
                      {formatDate(post.createdAt)}
                    </span>
                  </div>
                  
                  <h2 className="news-title">{post.title}</h2>
                  
                  <p className="news-excerpt">{excerpt}</p>
                  
                  {post.author && (
                    <div className="news-author">
                      <span>By {post.author}</span>
                    </div>
                  )}
                  
                  <button className="read-article-btn">
                    Read Article →
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Modal for full article */}
      {selectedPost && (
        <div className="news-modal" onClick={closePost}>
          <div className="news-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closePost}>×</button>
            
            {selectedPost.imageUrl && (
              <div className="modal-image">
                <img src={selectedPost.imageUrl} alt={selectedPost.title} />
              </div>
            )}
            
            <div className="modal-header">
              {selectedPost.category && (
                <span className={`category-badge ${selectedPost.category?.toLowerCase()}`}>
                  {selectedPost.category}
                </span>
              )}
              <h1 className="modal-title">{selectedPost.title}</h1>
              <div className="modal-meta">
                {selectedPost.author && <span className="author">By {selectedPost.author}</span>}
                <span className="date">{formatDate(selectedPost.createdAt)}</span>
              </div>
            </div>
            
            <div className="modal-body">
              <p className="news-content">{selectedPost.content}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
